
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Profile {
  id: string;
  auth_user_id: string | null;
  email: string;
  username?: string;
  first_name: string;
  last_name: string;
  role: 'superadmin' | 'member';
  team_id?: string;
  club_id?: string;
}

interface Organization {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
  website?: string;
}

interface OrganizationMembership {
  id: string;
  organization_id: string;
  user_id: string;
  status: 'invited' | 'accepted';
  organization: Organization;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isImpersonating: boolean;
  originalAdminProfile: Profile | null;
  impersonationSessionId: string | null;
  currentOrganization: Organization | null;
  userOrganizations: OrganizationMembership[];
  organizationLoading: boolean;
  effectiveRole: 'superadmin' | 'member';
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, userData: { first_name: string; last_name: string; username?: string }) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  startImpersonation: (targetUserId: string) => Promise<{ error: Error | null; shouldRedirect?: boolean }>;
  stopImpersonation: () => Promise<{ error: Error | null }>;
  switchOrganization: (organizationId: string) => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdminProfile, setOriginalAdminProfile] = useState<Profile | null>(null);
  const [impersonationSessionId, setImpersonationSessionId] = useState<string | null>(null);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<OrganizationMembership[]>([]);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [sessionRefreshCount, setSessionRefreshCount] = useState(0);
  const [isResumingImpersonation, setIsResumingImpersonation] = useState(false); // Prevent duplicate resumption
  const [impersonationHealthCheckInterval, setImpersonationHealthCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [impersonationRestoredFromStorage, setImpersonationRestoredFromStorage] = useState(false);

  // Fallback timeout to ensure loading is never stuck forever
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.warn('⚠️ LOADING_TIMEOUT_FALLBACK_TRIGGERED - forcing loading to false after 10 seconds');
        setLoading(false);
      }
    }, 10000); // 10 second fallback

    return () => {
      clearTimeout(timeoutId);
    };
  }, [loading]);

  // Validate impersonation session health
  const validateImpersonationSession = async (sessionId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('validate_impersonation_session', {
        session_id_param: sessionId
      });
      
      if (error) {
        console.error('Error validating impersonation session:', error);
        return false;
      }
      
      return data === true;
    } catch (error) {
      console.error('Exception validating impersonation session:', error);
      return false;
    }
  };

  // Force cleanup of stale sessions for current admin
  const forceCleanupAdminSessions = async (adminId: string): Promise<number> => {
    try {
      const { data, error } = await supabase.rpc('force_end_admin_impersonation_sessions', {
        admin_id_param: adminId
      });
      
      if (error) {
        console.error('Error cleaning up admin sessions:', error);
        return 0;
      }
      
      return data || 0;
    } catch (error) {
      console.error('Exception cleaning up admin sessions:', error);
      return 0;
    }
  };

  // Detect active impersonation sessions for current admin
  const detectActiveImpersonationSessions = async (adminProfile: Profile) => {
    const timestamp = new Date().toISOString();
    
    // Prevent multiple simultaneous detections
    if (isResumingImpersonation) {
      return null;
    }
    
    try {
      // Only check for superadmins
      if (adminProfile.role !== 'superadmin') {
        return null;
      }

      // Query for active impersonation sessions
      
      const { data, error } = await supabase
        .from('impersonation_sessions')
        .select(`
          *,
          impersonated_profile:profiles!impersonation_sessions_impersonated_user_id_fkey(*)
        `)
        .eq('admin_id', adminProfile.id)
        .eq('is_active', true)
        .is('ended_at', null)
        .order('created_at', { ascending: false })
        .limit(1);


      if (error) {
        console.error(`[${timestamp}] ❌ SESSION_DETECTION_ERROR:`, error);
        return null;
      }

      if (!data || data.length === 0) {
        return null;
      }

      const activeSession = data[0];

      // Validate the session before resuming
      const isValid = await validateImpersonationSession(activeSession.session_id);
      if (!isValid) {
        return null;
      }

      // Get the target profile details
      const targetProfile = activeSession.impersonated_profile;
      if (!targetProfile) {
        console.error(`[${timestamp}] ❌ TARGET_PROFILE_NOT_FOUND:`, { sessionId: activeSession.session_id });
        return null;
      }

      return {
        sessionId: activeSession.session_id,
        targetProfile,
        originalAdmin: adminProfile
      };
    } catch (error) {
      console.error(`[${timestamp}] 💥 SESSION_DETECTION_EXCEPTION:`, error);
      return null;
    }
  };

  // Resume active impersonation session
  const resumeImpersonationSession = async (sessionData: { sessionId: string; targetProfile: Profile; originalAdmin: Profile }) => {
    const timestamp = new Date().toISOString();

    // Prevent duplicate resumption
    if (isResumingImpersonation) {
      return true;
    }

    setIsResumingImpersonation(true);

    try {
      // Set impersonation state
      setIsImpersonating(true);
      setOriginalAdminProfile(sessionData.originalAdmin);
      setImpersonationSessionId(sessionData.sessionId);
      setProfile(sessionData.targetProfile);

      // Fetch organizations for the impersonated user
      await fetchUserOrganizations(sessionData.targetProfile.id);

      // Update session activity
      await updateSessionActivity(sessionData.sessionId);

      // Clear any conflicting localStorage state and set new one
      localStorage.removeItem('impersonationState');
      const impersonationState = {
        isActive: true,
        sessionId: sessionData.sessionId,
        originalAdmin: sessionData.originalAdmin,
        targetProfile: sessionData.targetProfile
      };
      localStorage.setItem('impersonationState', JSON.stringify(impersonationState));

      
      toast.success(`Resumed impersonation of ${sessionData.targetProfile.first_name} ${sessionData.targetProfile.last_name}`, {
        description: 'Your previous impersonation session has been automatically restored',
        duration: 5000,
        id: 'impersonation-resumed' // Prevent duplicate toasts with same ID
      });

      // Set loading to false since we've successfully resumed the session and set the profile
      setLoading(false);
      return true;
    } catch (error) {
      console.error(`[${timestamp}] ❌ FAILED_TO_RESUME_SESSION:`, error);
      toast.error('Failed to resume impersonation session');
      // Set loading to false even on failure
      setLoading(false);
      return false;
    } finally {
      setIsResumingImpersonation(false);
    }
  };

  // Update session activity heartbeat
  const updateSessionActivity = async (sessionId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('update_impersonation_session_activity', {
        session_id_param: sessionId
      });
      
      if (error) {
        console.error('Error updating session activity:', error);
        return false;
      }
      
      return data === true;
    } catch (error) {
      console.error('Exception updating session activity:', error);
      return false;
    }
  };

  // Recover from invalid impersonation state
  const recoverFromInvalidImpersonation = async () => {
    
    // Clear impersonation state
    setIsImpersonating(false);
    setOriginalAdminProfile(null);
    setImpersonationSessionId(null);
    localStorage.removeItem('impersonationState');
    
    // Clear health check interval
    if (impersonationHealthCheckInterval) {
      clearInterval(impersonationHealthCheckInterval);
      setImpersonationHealthCheckInterval(null);
    }
    
    // If we have the original admin profile, restore it
    if (originalAdminProfile) {
      setProfile(originalAdminProfile);
      await fetchUserOrganizations(originalAdminProfile.id);
    } else if (session?.user) {
      // Fallback: refetch profile from session
      const fetchedProfile = await fetchProfile(session.user.id);
      if (fetchedProfile) {
        setProfile(fetchedProfile);
        await fetchUserOrganizations(fetchedProfile.id);
      }
    }
    
    toast.error('Impersonation session was invalid and has been reset');
  };

  const fetchProfile = async (userId: string) => {
    const timestamp = new Date().toISOString();
    
    try {
      const query = supabase
        .from('profiles')
        .select('*')
        .eq('auth_user_id', userId);
      
      
      const { data, error, count } = await query.single();
      
      
      if (error) {
        console.error(`[${timestamp}] ❌ PROFILE_FETCH_ERROR:`, {
          userId,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
          errorHint: error.hint,
          fullError: error
        });
        return null;
      }
      
      
      return data;
    } catch (error) {
      console.error(`[${timestamp}] 💥 PROFILE_FETCH_EXCEPTION:`, {
        userId,
        error,
        errorType: typeof error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      return null;
    }
  };

  const fetchUserOrganizations = async (userId: string) => {
    try {
      setOrganizationLoading(true);
      
      const { data, error } = await supabase
        .from('organization_memberships')
        .select(`
          *,
          organization:organizations(*)
        `)
        .eq('user_id', userId)
        .eq('status', 'accepted');
      
      if (error) {
        return;
      }

      // Transform the data to match our interface
      const transformedData: OrganizationMembership[] = (data || []).map(item => ({
        id: item.id,
        organization_id: item.organization_id,
        user_id: item.user_id,
        status: item.status as 'invited' | 'accepted',
        organization: item.organization
      }));

      setUserOrganizations(transformedData);

      // Set current organization from localStorage or first available
      const savedOrgId = localStorage.getItem('currentOrganizationId');
      const targetOrg = savedOrgId 
        ? transformedData?.find(membership => membership.organization.id === savedOrgId)?.organization
        : transformedData?.[0]?.organization;

      if (targetOrg) {
        setCurrentOrganization(targetOrg);
        localStorage.setItem('currentOrganizationId', targetOrg.id);
      }
    } catch (error) {
      console.error('Error fetching user organizations:', error);
    } finally {
      setOrganizationLoading(false);
    }
  };

  const switchOrganization = async (organizationId: string) => {
    const targetMembership = userOrganizations.find(
      membership => membership.organization.id === organizationId
    );

    if (targetMembership) {
      setCurrentOrganization(targetMembership.organization);
      localStorage.setItem('currentOrganizationId', organizationId);
      toast.success(`Switched to ${targetMembership.organization.name}`);
    }
  };

  const stopImpersonation = async () => {
    try {
      if (!impersonationSessionId) {
        return { error: 'No active impersonation session' };
      }

      const { data, error } = await supabase.functions.invoke('stop-impersonation', {
        body: { session_id: impersonationSessionId }
      });

      if (error) {
        console.error('Stop impersonation error:', error);
        return { error };
      }

      // Restore original admin profile
      setProfile(originalAdminProfile);
      setIsImpersonating(false);
      setOriginalAdminProfile(null);
      setImpersonationSessionId(null);
      
      // Clear localStorage state
      localStorage.removeItem('impersonationState');

      // Reload organizations for the original admin
      if (originalAdminProfile) {
        await fetchUserOrganizations(originalAdminProfile.id);
      }
      
      toast.success('Stopped impersonation');
      return { error: null };
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      return { error };
    }
  };

  // Load impersonation state from localStorage on mount
  useEffect(() => {
    const savedImpersonationState = localStorage.getItem('impersonationState');
    if (savedImpersonationState) {
      try {
        const state = JSON.parse(savedImpersonationState);
        if (state.isActive && state.sessionId && state.originalAdmin && state.targetProfile) {
          // Validate the session before restoring
          validateImpersonationSession(state.sessionId).then(isValid => {
            if (isValid) {
              setIsImpersonating(true);
              setOriginalAdminProfile(state.originalAdmin);
              setImpersonationSessionId(state.sessionId);
              setProfile(state.targetProfile);
            } else {
              localStorage.removeItem('impersonationState');
              toast.error('Previous impersonation session expired');
            }
            setImpersonationRestoredFromStorage(true);
          });
        } else {
          setImpersonationRestoredFromStorage(true);
        }
      } catch (error) {
        console.error('❌ FAILED_TO_RESTORE_IMPERSONATION_STATE:', error);
        localStorage.removeItem('impersonationState');
        setImpersonationRestoredFromStorage(true);
      }
    } else {
      setImpersonationRestoredFromStorage(true);
    }
  }, []);

  // Save impersonation state to localStorage whenever it changes
  useEffect(() => {
    if (isImpersonating && impersonationSessionId && originalAdminProfile && profile) {
      const impersonationState = {
        isActive: true,
        sessionId: impersonationSessionId,
        originalAdmin: originalAdminProfile,
        targetProfile: profile
      };
      localStorage.setItem('impersonationState', JSON.stringify(impersonationState));
    } else {
      localStorage.removeItem('impersonationState');
    }
  }, [isImpersonating, impersonationSessionId, originalAdminProfile, profile]);

  useEffect(() => {
    
    if (!impersonationRestoredFromStorage) {
      return;
    }
    
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const timestamp = new Date().toISOString();
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          
          // Only clear impersonation state on explicit sign out, not on token refresh
          if (isImpersonating && event === 'SIGNED_OUT') {
            setIsImpersonating(false);
            setOriginalAdminProfile(null);
            setImpersonationSessionId(null);
            localStorage.removeItem('impersonationState');
          }
          
          // Skip profile fetching if impersonation is active
          if (isImpersonating) {
            return;
          }
          
          // Only fetch profile if we don't have one or if it's for a different user
          const shouldFetchProfile = !profile || profile.auth_user_id !== session.user.id;
          
           if (shouldFetchProfile) {
             // Defer profile and organization fetching to avoid blocking auth state changes
             setTimeout(async () => {
               const fetchedProfile = await fetchProfile(session.user.id);
               if (fetchedProfile) {
                  setProfile(fetchedProfile);
                  await fetchUserOrganizations(fetchedProfile.id);
                  
                  // Now that profile is set, we can finish loading
                  setLoading(false);
                  
                  // Skip impersonation check here - it will be handled by initial session check
               } else {
                 // Even if profile fetch fails, we need to set loading to false
                 setLoading(false);
               }
             }, 0);
           } else {
             // Profile already exists and matches, so we can finish loading
             setLoading(false);
           }
        } else {
          // Clear all state when session is null
          setProfile(null);
          setCurrentOrganization(null);
          setUserOrganizations([]);
          setIsImpersonating(false);
          setOriginalAdminProfile(null);
          setImpersonationSessionId(null);
          localStorage.removeItem('currentOrganizationId');
          localStorage.removeItem('impersonationState');
          // For no session, we can immediately set loading to false
          setLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const timestamp = new Date().toISOString();
      
      setSession(session);
      setUser(session?.user ?? null);
      
       if (session?.user) {
         setTimeout(async () => {
           const fetchedProfile = await fetchProfile(session.user.id);
           if (fetchedProfile) {
             
             // Check for active impersonation sessions BEFORE setting profile
             if (fetchedProfile.role === 'superadmin') {
               const activeSessionData = await detectActiveImpersonationSessions(fetchedProfile);
               if (activeSessionData) {
                 await resumeImpersonationSession(activeSessionData);
                 // setLoading(false) will be called by resumeImpersonationSession
                 return; // Exit early, resumeImpersonationSession will set the impersonated profile
               }
             }
             
             // Only set admin profile if no impersonation session was resumed
             setProfile(fetchedProfile);
             await fetchUserOrganizations(fetchedProfile.id);
             
             // Now that profile is set, we can finish loading
             setLoading(false);
           } else {
             // Even if profile fetch fails, we need to set loading to false
             setLoading(false);
           }
         }, 0);
       } else {
         // No session means no user, can immediately set loading to false
         setLoading(false);
       }
    }).catch(error => {
      console.error('❌ INITIAL_SESSION_CHECK_ERROR:', error);
      setLoading(false); // Ensure loading is set to false even if session check fails
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonationRestoredFromStorage]); // Remove profile dependency to prevent re-initialization

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, userData: { first_name: string; last_name: string; username?: string }) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: userData.first_name,
          last_name: userData.last_name,
          username: userData.username || email,
        },
      },
    });
    return { error };
  };

  const signOut = async () => {
    // Clear impersonation state and organization data before signing out
    setIsImpersonating(false);
    setOriginalAdminProfile(null);
    setImpersonationSessionId(null);
    setCurrentOrganization(null);
    setUserOrganizations([]);
    localStorage.removeItem('currentOrganizationId');
    
    await supabase.auth.signOut();
    setProfile(null);
  };

  // Session refresh function
  const refreshSession = async (): Promise<boolean> => {
    const timestamp = new Date().toISOString();

    try {
      const { data: { session: refreshedSession }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error(`[${timestamp}] ❌ SESSION_REFRESH_ERROR:`, error);
        return false;
      }

      if (refreshedSession) {
        
        setSession(refreshedSession);
        setUser(refreshedSession.user);
        setSessionRefreshCount(count => count + 1);
        return true;
      }

      return false;
    } catch (error) {
      console.error(`[${timestamp}] 💥 SESSION_REFRESH_EXCEPTION:`, error);
      return false;
    }
  };

  // Session health monitor
  useEffect(() => {
    if (!session) return;

    const checkSessionHealth = async () => {
      const timestamp = new Date().toISOString();
      
      // Check if session is about to expire (within 5 minutes)
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = expiresAt ? (expiresAt - now) : 0;
      

      if (timeUntilExpiry < 300 && timeUntilExpiry > 0) {
        await refreshSession();
      }
    };

    // Check session health every 30 seconds
    const healthCheckInterval = setInterval(checkSessionHealth, 30000);
    
    // Initial health check
    checkSessionHealth();

    return () => clearInterval(healthCheckInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]); // refreshSession intentionally omitted to prevent infinite re-renders

  // Impersonation health check effect
  useEffect(() => {
    if (!isImpersonating || !impersonationSessionId) {
      // Clear any existing health check interval
      if (impersonationHealthCheckInterval) {
        clearInterval(impersonationHealthCheckInterval);
        setImpersonationHealthCheckInterval(null);
      }
      return;
    }


    // Validate session immediately
    validateImpersonationSession(impersonationSessionId).then(isValid => {
      if (!isValid) {
        recoverFromInvalidImpersonation();
        return;
      }
    });

    // Set up periodic health check and activity update
    const healthCheck = async () => {
      if (!impersonationSessionId) return;
      
      const isValid = await validateImpersonationSession(impersonationSessionId);
      if (!isValid) {
        recoverFromInvalidImpersonation();
        return;
      }
      
      // Update activity heartbeat
      await updateSessionActivity(impersonationSessionId);
    };

    // Check every 2 minutes
    const interval = setInterval(healthCheck, 120000);
    setImpersonationHealthCheckInterval(interval);

    // Cleanup function
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImpersonating, impersonationSessionId]); // Other functions intentionally omitted to prevent infinite re-renders

  const startImpersonation = async (targetUserId: string) => {
    const timestamp = new Date().toISOString();
    try {
      
      // Validate and refresh session before attempting impersonation
      const sessionValid = await refreshSession();
      if (!sessionValid) {
        toast.error('Session expired. Please sign in again.');
        return { error: 'Session expired' };
      }
      
      // First, cleanup any existing stale sessions for this admin
      if (profile?.id) {
        await forceCleanupAdminSessions(profile.id);
      }
      
      const { data, error } = await supabase.functions.invoke('start-impersonation', {
        body: { target_user_id: targetUserId }
      });


      if (error) {
        console.error('Impersonation function error:', error);
        toast.error(`Failed to start impersonation: ${error.message}`);
        return { error };
      }

      if (data?.error) {
        console.error('Server error:', data.error);
        toast.error(`Failed to start impersonation: ${data.error}`);
        return { error: data.error };
      }

      if (!data?.success) {
        console.error('Impersonation failed - no success flag');
        toast.error('Failed to start impersonation - invalid response');
        return { error: 'Invalid response from server' };
      }

      // Store the original admin profile and set impersonation state
      setOriginalAdminProfile(data.admin_profile);
      setProfile(data.target_profile);
      setIsImpersonating(true);
      setImpersonationSessionId(data.session_id);
      
      // Fetch organizations for the impersonated user
      await fetchUserOrganizations(data.target_profile.id);
      
      // Don't show toast here - let the calling component handle it
      
      // Return flag to indicate redirect should happen
      return { error: null as any, shouldRedirect: true };
    } catch (error) {
      console.error('Error starting impersonation:', error);
      toast.error('Failed to start impersonation - network error');
      return { error };
    }
  };

  // Compute effective role for navigation and route protection
  const effectiveRole = isImpersonating && originalAdminProfile 
    ? originalAdminProfile.role 
    : profile?.role || 'member';

  const value = {
    user,
    session,
    profile,
    loading,
    isImpersonating,
    originalAdminProfile,
    impersonationSessionId,
    currentOrganization,
    userOrganizations,
    organizationLoading,
    effectiveRole,
    signIn,
    signUp,
    signOut,
    startImpersonation,
    stopImpersonation,
    switchOrganization,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
